// PurchaseOrderVendorParty.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useBase } from 'context/BaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useSubscribedNode from 'hooks/useSubscribedNode';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';
import useRelationQuickAdd from 'node/form/fields/nodelist/useRelationQuickAdd';

// Utilities
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { getNodelistNodeTitle } from 'utilities/NodelistField';
import { applyCatalogLiveRead } from 'utilities/PlatformCatalog';
import {
  getPurchaseOrderSchemaField,
  getPurchaseOrderVendorKey,
  getVendorPartyDisplay,
} from 'nodetypes/purchaseorder/view/purchaseOrderPartyUtils';

// CSS
import './PurchaseOrderGeneralInfo.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import PurchaseOrderPartyDetails from 'nodetypes/purchaseorder/view/PurchaseOrderPartyDetails';
import RelationQuickAddModal from 'node/form/fields/nodelist/RelationQuickAddModal';

const VENDOR_TYPE_KEY = 'VENDOR';

/**
 * PurchaseOrderVendorParty
 *
 * Houzz-style single vendor picker + hydrated address block.
 */
const PurchaseOrderVendorParty = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  relatedNodesByKey = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();
  const { getType } = useNodeTypes();

  const baseKey = node?.baseKey || activeBase?.key || null;
  const typeKey = String(nodeType?.key || node?.type || 'PURCHASE_ORDER').trim().toUpperCase();
  const vendorField = useMemo(
    () => getPurchaseOrderSchemaField(nodeType, 'vendor'),
    [nodeType],
  );
  const targetType = String(vendorField?.targetType || VENDOR_TYPE_KEY).trim() || VENDOR_TYPE_KEY;
  const selectedKey = getPurchaseOrderVendorKey(node, nodeType);
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const overrideNode = relatedNodesByKey && selectedKey
    ? (relatedNodesByKey[selectedKey] || null)
    : null;

  // ----- HOOKS ----- //

  const {
    options: vendorOptions,
    resolveOptionValue,
    catalogById,
    nodesByKey,
  } = useNodelistTargetOptions({
    targetType,
    baseKey,
    enabled: Boolean(baseKey && targetType),
  });

  const subscribedVendor = useSubscribedNode(selectedKey, {
    enabled: Boolean(selectedKey) && !overrideNode,
  });
  const selectedVendor = useMemo(() => {
    const raw = overrideNode
      || (selectedKey && nodesByKey.has(selectedKey) ? nodesByKey.get(selectedKey) : null)
      || subscribedVendor;
    if (!raw) return null;
    const catalogId = String(raw.catalogId || '').trim();
    if (!catalogId) return raw;
    const entry = catalogById.get(catalogId);
    if (!entry) return raw;
    return applyCatalogLiveRead(raw, entry, getType(targetType)) || raw;
  }, [catalogById, getType, nodesByKey, overrideNode, selectedKey, subscribedVendor, targetType]);

  // ----- MEMOS ----- //

  const display = useMemo(
    () => getVendorPartyDisplay(selectedVendor),
    [selectedVendor],
  );

  const baseSelectOptions = useMemo(() => {
    const empty = { value: '', label: 'Select a vendor…' };
    const byValue = new Map(
      (Array.isArray(vendorOptions) ? vendorOptions : []).map((option) => [
        String(option.value || '').trim(),
        option,
      ]),
    );

    if (selectedKey && !byValue.has(selectedKey)) {
      byValue.set(selectedKey, {
        value: selectedKey,
        label: selectedVendor
          ? getNodelistNodeTitle(selectedVendor)
          : 'Unavailable vendor',
      });
    }

    return [empty, ...byValue.values()];
  }, [selectedKey, selectedVendor, vendorOptions]);

  // ----- HANDLERS ----- //

  const handleVendorChange = useCallback(async (event) => {
    if (!canEdit) return;
    const raw = String(event?.target?.value ?? event ?? '').trim();
    let nextKey = '';
    try {
      nextKey = raw ? await resolveOptionValue(raw) : '';
    } catch (error) {
      console.error('[PurchaseOrderVendorParty] catalog adopt failed:', error);
      return;
    }
    const field = vendorField || {
      name: 'vendor',
      source: typeKey.toLowerCase(),
      type: 'relation',
      targetType,
      allowMultiple: false,
    };
    const patch = buildNodeFieldPatch(
      node,
      field,
      typeKey,
      nextKey ? [nextKey] : [],
    );
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, onNodeChange, resolveOptionValue, targetType, typeKey, vendorField]);

  const handleSelect = useCallback((nextKey) => {
    handleVendorChange(nextKey);
  }, [handleVendorChange]);

  const {
    footerActionLabel,
    onFooterAction,
    selectOptions,
    quickAddOpen,
    setQuickAddOpen,
    quickAddInitialTitle,
    handleCreated,
  } = useRelationQuickAdd({
    readOnly: !canEdit,
    targetType,
    baseKey,
    options: baseSelectOptions,
    onSelect: handleSelect,
  });

  // ----- RENDER ----- //

  return (
    <div className="po-general-info-party">
      <div className="po-general-info-label">Vendor</div>

      {canEdit ? (
        <div className="po-general-info-select">
          <SelectInput
            value={selectedKey}
            options={selectOptions}
            placeholder="Select a vendor…"
            showIcons={false}
            showFilter={vendorField?.showFilter === true}
            footerActionLabel={footerActionLabel}
            onFooterAction={onFooterAction}
            width="100%"
            hybrid
            style={{
              fontWeight: 500,
              height: '32px',
            }}
            onChange={handleVendorChange}
          />
        </div>
      ) : (
        <div className={`po-general-info-value${display.title ? '' : ' is-empty'}`}>
          {display.title || (canEdit ? 'Select a vendor…' : '—')}
        </div>
      )}

      <PurchaseOrderPartyDetails
        relatedNode={selectedVendor}
        address={display.address}
        phone={display.phone}
        email={display.email}
        editLabel="Edit vendor"
      />

      {canEdit ? (
        <RelationQuickAddModal
          isOpen={quickAddOpen}
          setOpen={setQuickAddOpen}
          typeKey={targetType}
          baseKey={baseKey}
          parentNode={node}
          initialTitle={quickAddInitialTitle}
          onCreated={handleCreated}
        />
      ) : null}
    </div>
  );
});

PurchaseOrderVendorParty.displayName = 'PurchaseOrderVendorParty';

export default PurchaseOrderVendorParty;
